/**
 * Minimal PNG codec: palette (colour type 3) and RGBA (colour type 6),
 * 8-bit, no interlace. Used for daily tile snapshots in R2, tile renders,
 * and the globe texture. Runs anywhere CompressionStream exists (Workers,
 * browsers, Node ≥ 18).
 */

const SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array, start = 0, end = bytes.length): number {
  let c = 0xffffffff;
  for (let i = start; i < end; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function zlibDeflate(bytes: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate');
  const w = cs.writable.getWriter();
  void w.write(bytes as Uint8Array<ArrayBuffer>);
  void w.close();
  return new Uint8Array(await new Response(cs.readable).arrayBuffer());
}

async function zlibInflate(bytes: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const w = ds.writable.getWriter();
  void w.write(bytes as Uint8Array<ArrayBuffer>);
  void w.close();
  return new Uint8Array(await new Response(ds.readable).arrayBuffer());
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  out[4] = type.charCodeAt(0);
  out[5] = type.charCodeAt(1);
  out[6] = type.charCodeAt(2);
  out[7] = type.charCodeAt(3);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out, 4, 8 + data.length));
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function ihdr(width: number, height: number, colourType: number): Uint8Array {
  const d = new Uint8Array(13);
  const v = new DataView(d.buffer);
  v.setUint32(0, width);
  v.setUint32(4, height);
  d[8] = 8; // bit depth
  d[9] = colourType;
  d[10] = 0; // compression
  d[11] = 0; // filter
  d[12] = 0; // interlace
  return d;
}

/** Add the per-scanline filter byte (0 = None). */
function addFilterBytes(data: Uint8Array, width: number, height: number, bpp: number): Uint8Array {
  const stride = width * bpp;
  const out = new Uint8Array((stride + 1) * height);
  for (let y = 0; y < height; y++) {
    out[y * (stride + 1)] = 0;
    out.set(data.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1);
  }
  return out;
}

/**
 * Encode an indexed PNG. `palette` is 256 × RGB bytes; `transparentIndex`
 * (default 0) becomes fully transparent via a tRNS chunk.
 */
export async function encodeIndexedPNG(
  width: number,
  height: number,
  indices: Uint8Array,
  palette: Uint8Array,
  transparentIndex: number | null = 0,
): Promise<Uint8Array> {
  if (indices.length !== width * height) throw new RangeError('indices length mismatch');
  const parts: Uint8Array[] = [SIGNATURE, chunk('IHDR', ihdr(width, height, 3)), chunk('PLTE', palette.subarray(0, 768))];
  if (transparentIndex !== null) {
    const trns = new Uint8Array(transparentIndex + 1).fill(255);
    trns[transparentIndex] = 0;
    parts.push(chunk('tRNS', trns));
  }
  parts.push(chunk('IDAT', await zlibDeflate(addFilterBytes(indices, width, height, 1))));
  parts.push(chunk('IEND', new Uint8Array(0)));
  return concat(parts);
}

export async function encodeRGBAPNG(width: number, height: number, rgba: Uint8Array): Promise<Uint8Array> {
  if (rgba.length !== width * height * 4) throw new RangeError('rgba length mismatch');
  const parts: Uint8Array[] = [
    SIGNATURE,
    chunk('IHDR', ihdr(width, height, 6)),
    chunk('IDAT', await zlibDeflate(addFilterBytes(rgba, width, height, 4))),
    chunk('IEND', new Uint8Array(0)),
  ];
  return concat(parts);
}

export interface DecodedPNG {
  width: number;
  height: number;
  colourType: number;
  bitDepth: number;
  /** Unfiltered samples, `width * height * channels` bytes. */
  data: Uint8Array;
  palette: Uint8Array | null;
}

function paeth(a: number, b: number, c: number): number {
  const p = a + b - c;
  const pa = Math.abs(p - a);
  const pb = Math.abs(p - b);
  const pc = Math.abs(p - c);
  if (pa <= pb && pa <= pc) return a;
  if (pb <= pc) return b;
  return c;
}

export async function decodePNG(bytes: Uint8Array): Promise<DecodedPNG> {
  for (let i = 0; i < 8; i++) if (bytes[i] !== SIGNATURE[i]) throw new Error('not a PNG');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 8;
  let width = 0;
  let height = 0;
  let colourType = 0;
  let bitDepth = 0;
  let palette: Uint8Array | null = null;
  const idat: Uint8Array[] = [];
  while (o < bytes.length) {
    const len = view.getUint32(o);
    const type = String.fromCharCode(bytes[o + 4], bytes[o + 5], bytes[o + 6], bytes[o + 7]);
    const data = bytes.subarray(o + 8, o + 8 + len);
    if (type === 'IHDR') {
      width = view.getUint32(o + 8);
      height = view.getUint32(o + 12);
      bitDepth = bytes[o + 16];
      colourType = bytes[o + 17];
      if (bytes[o + 20] !== 0) throw new Error('interlaced PNG not supported');
      if (bitDepth !== 8) throw new Error('only 8-bit PNG supported');
    } else if (type === 'PLTE') {
      palette = new Uint8Array(768);
      palette.set(data.subarray(0, 768));
    } else if (type === 'IDAT') {
      idat.push(data);
    } else if (type === 'IEND') {
      break;
    }
    o += 12 + len;
  }
  const channels = colourType === 3 ? 1 : colourType === 6 ? 4 : colourType === 2 ? 3 : colourType === 0 ? 1 : colourType === 4 ? 2 : 0;
  if (!channels) throw new Error(`unsupported colour type ${colourType}`);
  const raw = await zlibInflate(concat(idat));
  const stride = width * channels;
  const data = new Uint8Array(stride * height);
  let prev = new Uint8Array(stride);
  for (let y = 0; y < height; y++) {
    const filter = raw[y * (stride + 1)];
    const line = raw.subarray(y * (stride + 1) + 1, (y + 1) * (stride + 1));
    const cur = data.subarray(y * stride, (y + 1) * stride);
    for (let x = 0; x < stride; x++) {
      const a = x >= channels ? cur[x - channels] : 0;
      const b = prev[x];
      const c = x >= channels ? prev[x - channels] : 0;
      let v = line[x];
      switch (filter) {
        case 0:
          break;
        case 1:
          v += a;
          break;
        case 2:
          v += b;
          break;
        case 3:
          v += (a + b) >> 1;
          break;
        case 4:
          v += paeth(a, b, c);
          break;
        default:
          throw new Error(`bad filter ${filter}`);
      }
      cur[x] = v & 0xff;
    }
    prev = cur;
  }
  return { width, height, colourType, bitDepth, data, palette };
}

/** Decode an indexed PNG produced by {@link encodeIndexedPNG}; returns the indices. */
export async function decodeIndexedPNG(bytes: Uint8Array): Promise<{ width: number; height: number; indices: Uint8Array }> {
  const png = await decodePNG(bytes);
  if (png.colourType !== 3) throw new Error('not an indexed PNG');
  return { width: png.width, height: png.height, indices: png.data };
}

/** Expand palette indices to RGBA using a 256 × RGBA palette. */
export function indicesToRGBA(indices: Uint8Array, paletteRGBA: Uint8Array): Uint8Array {
  const out = new Uint8Array(indices.length * 4);
  for (let i = 0; i < indices.length; i++) {
    const p = indices[i] * 4;
    out[i * 4] = paletteRGBA[p];
    out[i * 4 + 1] = paletteRGBA[p + 1];
    out[i * 4 + 2] = paletteRGBA[p + 2];
    out[i * 4 + 3] = paletteRGBA[p + 3];
  }
  return out;
}
